import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { authSelectors } from '../../../../store/selectors/auth';
import { profileSelectors } from '../../../../store/selectors/profile';
import { editUserInfoThunk, getProfileThunk } from '../../../../store/thunks/profile';
import actions from '../../../../store/actions/index';

import SelectEl from '../../../SelectEl/SelectEl';

/**
 * Часовий пояс, у якому кабінет показує розклад і час у нагадуваннях.
 *
 * Зберігаємо IANA-ідентифікатор ('America/New_York'), а не «+/- N годин»: зсув приходить
 * з бекенду вже порахований на поточну дату, тому перехід на літній/зимовий час нічого
 * не ламає і руками нічого міняти не треба.
 */

/** Порожній пояс у профілі = київський час (дефолт школи). */
const SCHOOL_TIMEZONE = 'Europe/Kyiv';

const TimezoneSelect = () => {
  const dispatch = useDispatch();

  const auth = useSelector(authSelectors);
  const profile = useSelector(profileSelectors);

  const token = auth.token;
  const user = profile.user;
  const timezones = profile.timezones;

  const [selectedValue, setSelectedValue] = useState(null);

  const options = useMemo(
    () => (timezones || []).map((zone) => ({ value: zone.value, label: zone.label })),
    [timezones]
  );

  // Залежність саме від усього `user`, а не від `user.timezone`: після невдалого збереження
  // профіль перечитується з тим самим поясом, і селект має відкотитись до нього.
  useEffect(() => {
    setSelectedValue(user.timezone || SCHOOL_TIMEZONE);
  }, [user]);

  const selectedOption = useMemo(
    () => options.find((option) => option.value === selectedValue) || null,
    [options, selectedValue]
  );

  const handleSelect = (value) => {
    if (!value || !token || value === selectedValue) {
      return;
    }

    // Показуємо вибір одразу: запит іде у фоні, а селект не має «відкочуватись».
    setSelectedValue(value);

    // Кеш розкладу порахований під старий пояс — без скидання студент бачив би
    // старі години, поки кеш місяця не протухне.
    dispatch(actions.schedule.resetSchedule());

    // Профіль перечитуємо в будь-якому разі: при успіху — щоб підтягнути новий пояс,
    // при помилці — щоб селект відкотився до збереженого значення.
    dispatch(editUserInfoThunk({ value, field: 'timezone', token }))
      .catch(() => null)
      .then(() => dispatch(getProfileThunk({ token })));
  };

  return (
    <SelectEl
      options={options}
      setSelected={handleSelect}
      selectedOption={selectedOption}
      isDisabled={!options.length}
      placeholder="Оберіть місто"
    />
  );
};

export default TimezoneSelect;
